'use strict';
/**
 *
 * @param propertiesArray properties Array. eg this = {p1: {pp1: 'value'}} => ['p1', 'pp1'].
 * @param object object to extract value from.
 * @param defaultValue default value doesn't result any concrete value, this will be resulted. `false` IS a concrete value.
 * @return {null|*}
 */
exports.getSafely = (propertiesArray, object, defaultValue = undefined) => {
    if (!propertiesArray || !object || !Array.isArray(propertiesArray)) {
        return defaultValue;
    }

    const toReturn = propertiesArray.reduce((acc, currentProp) => {
        return ((acc && exports.isConcreteValue(acc[currentProp])) ? acc[currentProp] : undefined);
    }, object);


    if (exports.isUndefined(defaultValue)) {
        return toReturn;
    }

    return toReturn || defaultValue;
};

exports.isString = (val) => typeof val === 'string';

exports.isConcreteValue = (val, allowEmptyObject = true) => typeof val !== 'undefined' && val !== null && !exports.isEmptyString(val) && (!Array.isArray(val) || val.length > 0) &&
    (allowEmptyObject || exports.getSafely(['constructor'], val) !== Object || Object.keys(val).length > 0);

exports.isEmptyString = (val) => exports.isString(val) && val.trim().length === 0;

exports.isNonEmptyString = (val) => exports.isString(val) && val.trim().length > 0;

exports.valueOrDefault = (val, ...defaultValue) =>
    exports.isConcreteValue(val) ? val : defaultValue.find((x) => !exports.isUndefined(x));

exports.valueOrZero = (val) => val || 0;

exports.valueOrEmptyString = (val) => val || '';

exports.nonZeroOrNull = (val) => (val !== 0 ? val : null);

exports.isNonEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;

exports.isNonEmptyObject = (obj) => typeof (obj) === 'object' && exports.isConcreteValue(obj) && Object.keys(obj).length > 0;

exports.isFunction = (func) => exports.isConcreteValue(func) && {}.toString.call(func) === '[object Function]';

exports.isNumber = (value) => typeof value === 'number' && !isNaN(value);

/**
 * Sets an object field in case both the object and its value or concrete.
 *
 * @param obj object
 * @param field field name
 * @param optionalValue the optional value to set if its concrete.
 */
exports.setIfHasValue = (obj, field, optionalValue) => {
    if ((exports.isConcreteValue(optionalValue) || Array.isArray(optionalValue))
        && exports.isConcreteValue(obj)) {
        obj[field] = optionalValue;
    }

    return obj;
};

exports.setIfEmpty = (obj, field, value) => {
    if (exports.allConditionsAreMet(exports.isConcreteValue(obj),
        exports.isConcreteValue(value), !exports.getSafely([field], obj))) {
        obj[field] = value;
    }

    return obj;
};

exports.digitsOnly = (input) => typeof input === 'number'
    ? input
    : exports.valueOrEmptyString(input).replace(/\D/g, '');

exports.removeNewLines = (val) => typeof val === 'string' ? val.replace(/\r?\n|\r/g, '') : '';

exports.toLowerCaseFirstLetter = (val) => typeof val === 'string' ? val[0].toLowerCase() +
    (val.length > 1 ? val.substr(1) : '') : '';

exports.cleanArr = (arr) => {
    if (!Array.isArray(arr)) {
        return arr;
    }

    let toReturn = arr.map((item) => {
        if (Array.isArray(item)) {
            return exports.cleanArr(item);
        } else if (typeof item === 'object') {
            return exports.cleanObj(item, true);
        } else {
            return item;
        }
    })

    return toReturn.filter((item) => exports.isConcreteValue(item, false));
}

exports.cleanObj = (obj, recursively = false) => {
    if (!exports.isConcreteValue(obj)) {
        return obj;
    }

    return Object.keys(obj).reduce((acc, key) => {
        let valueForKey = obj[key];
        if (!exports.isConcreteValue(valueForKey, false)) {
            valueForKey = undefined;
        } else if (recursively) {
            if (exports.isNonEmptyArray(valueForKey)) {
                valueForKey = exports.cleanArr(valueForKey);
                if (!exports.isNonEmptyArray(valueForKey)) {
                    valueForKey = undefined;
                }
            } else if (exports.isNonEmptyObject(valueForKey)) {
                valueForKey = exports.cleanObj(valueForKey, recursively);
                if (!exports.isNonEmptyObject(valueForKey)) {
                    valueForKey = undefined;
                }
            }
        }

        if (exports.isConcreteValue(valueForKey, false)) {
            valueForKey = {[key]: valueForKey};
        }

        return ({...acc, ...valueForKey});
    }, {});
};

exports.moveObjPropsToCamelCase = (obj) => {
    if (!exports.isConcreteValue(obj)) {
        return obj;
    }

    return Object.keys(obj).reduce((acc, key) => ({...acc, [exports.toLowerCaseFirstLetter(key)]: obj[key]}), {});
};

exports.xor = (a, b) => (a && !b) || (!a && b);

exports.allConditionsAreMet = (...args) => !args.some((condition) => !condition);

exports.anyOfConditionsAreMet = (...args) => args.some((condition) => !!condition);

exports.trimmed = (val) => exports.isString(val) ? val.trim() : val;

/**
 * This methods wrap an async method and performs it in the background while ignoring errors if needed.
 *
 * @param toPerform delegate to perform.
 * @param sync will await for the delegate to perform.
 * @param ignoreErrors will ignore errors.
 * @return {Promise<*>}
 */
exports.performSyncIfNeeded = async (toPerform, sync = true, ignoreErrors = false) => {
    if (sync) {
        return await toPerform();
    }

    return new Promise((resolve) => {
        resolve();
        (async function () {
            if (!ignoreErrors) {
                return await toPerform();
            }

            try {
                return await toPerform();
            } catch (e) {
                return false;
            }
        }());
    });
};

/**
 * This method bind the scope of execution and performs the delegate only if the prop value is concrete.
 * @param delegate
 * @return {(function(*=): (*|undefined))|*}
 */
exports.wrapPerformIfValueIsConcrete = (delegate) => {
    return (val) => {
        if (!exports.isConcreteValue(val)) {
            return val;
        }

        return delegate(val);
    }
}

/**
 * This method chains onto and object and performs its delegate according to its condition.
 * @param chainTo
 * @param condition
 * @param iChainDelegateWhenTrue optional method when condition is true.
 * @param iChainDelegateWhenFalse optional method when condition is false.
 * @return {*}
 */
exports.chainOnCondition = (chainTo, condition, iChainDelegateWhenTrue, iChainDelegateWhenFalse) => {
    const chainWhenTrue = exports.valueOrDefault(iChainDelegateWhenTrue, () => chainTo);
    const chainWhenFalse = exports.valueOrDefault(iChainDelegateWhenFalse, () => chainTo);
    return condition ? chainWhenTrue(chainTo) : chainWhenFalse(chainTo);
};

exports.performIfDelegate = (delegate, ...props) => {
    if (exports.isFunction(delegate)) {
        return delegate.call(this, ...props);
    }

    return false;
}

exports.isUndefined = (obj) => obj === void 0;

/**
 * Returns either truthy of falsy value depend on the condition.
 * @param condition boolean condition or function to evaluate.
 * @param ifTrue value or function
 * @param ifFalse value or function
 * @return {*}
 */
exports.resolveCondition = (condition, ifTrue = undefined, ifFalse = undefined) => {
    let conditionResult = !!condition;
    if (exports.isFunction(condition)) {
        conditionResult = condition();
    }

    let result;
    if (conditionResult) {
        result = ifTrue;
        if (exports.isFunction(ifTrue)) {
            result = ifTrue();
        }
    } else {
        result = ifFalse;
        if (exports.isFunction(ifFalse)) {
            return ifFalse();
        }
    }

    return result;
}

/**
 * Sets the value of a field if it was changed.
 *
 * @param obj
 * @param field
 * @param value
 */
exports.setIfDifferent = (obj, field, value) => {
    if (exports.getSafely([field], obj) === value) {
        return;
    }

    obj[field] = value;
}

/**
 * Wrap item in array if not already an array.
 * In case undefined or null, returns empty array.
 *
 * @param itemOrArray
 * @returns [*]
 */
exports.wrapInArray = (itemOrArray) => Array.isArray(itemOrArray) ? itemOrArray : (exports.isConcreteValue(itemOrArray) ? [itemOrArray] : []);

/**
 * Returns weather or not a value is of type boolean and concrete valid value.
 * @param val
 * @returns {boolean}
 */
exports.isBoolean = (val) => val === true || val === false;

exports.awaitTimeout = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));
